import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import { TenantContextProvider } from "./lib/tenant-context";
import { DensityProvider } from "./hooks/use-density";
import { initPostHog } from "./lib/posthog";
import { initSentry } from "./lib/sentry";
import "./styles.css";
import { runDiagnostics, renderDiagnosticsBanner } from "./lib/env-diagnostics";

// PostHog — activated when VITE_POSTHOG_API_KEY is set
initPostHog();

// Sentry — activated when VITE_SENTRY_DSN is set
initSentry();

// Env diagnostics — show banner if critical env vars are missing
const envDiag = runDiagnostics();
const envBanner = renderDiagnosticsBanner(envDiag);
if (envBanner) {
  // eslint-disable-next-line no-console
  console.warn("[DIKDOC env banner]", envBanner);
  // Insert visible banner before React mounts
  const banner = document.createElement("div");
  banner.id = "env-diagnostics-banner";
  banner.style.cssText =
    "position:fixed;bottom:0;left:0;right:0;z-index:99999;background:#f59e0b;color:#000;padding:12px 16px;font-family:system-ui,sans-serif;font-size:14px;text-align:center;box-shadow:0 -2px 8px rgba(0,0,0,0.3)";
  banner.textContent = envBanner;
  document.body.appendChild(banner);
}

// Global error handlers — surface full error details to console for debugging
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    // eslint-disable-next-line no-console
    console.error("[DIKDOC global error]", e.error || e.message, e.filename + ":" + e.lineno + ":" + e.colno);
  });
  window.addEventListener("unhandledrejection", (e) => {
    // eslint-disable-next-line no-console
    console.error("[DIKDOC unhandled rejection]", e.reason);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TenantContextProvider>
          <DensityProvider>
            <RouterProvider router={router} />
          </DensityProvider>
        </TenantContextProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
